import { useState, useEffect, useRef } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { useEmailStore } from '@/stores/useEmailStore';
import { useAuth } from '@/contexts/SupabaseAuthContext';
import { supabase } from '@/lib/customSupabaseClient';
import { wrapInTemplate, extractVimeoId } from '@/lib/emailTemplate';
import { BrandedPreviewIframe } from '@/components/email/BrandedPreviewIframe';
import { uploadEmailImage, isSupportedImage } from '@/lib/emailImages';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import { Sparkles, Send, TestTube, Loader2, Users, Wand2, PenLine, Eye, Code, AlertCircle, Megaphone, Video, Plus, X, ImagePlus, Upload, ShieldAlert, Mail } from 'lucide-react';
import { toast } from 'sonner';

export function ComposeDialog({ prefilledUserId, prefilledName, prefilledEmail, prefilledProgramId, prefilledProgramName, prefilledDraft }) {
  const {
    programs, fetchPrograms,
    sendEmail, sending,
    generateWithAI, polishWithAI, generating, generatedEmails, clearGeneratedEmails,
    checkRecentBulkSend,
  } = useEmailStore();
  const { can } = useAuth();
  const canBroadcast = can('email.broadcast');
  const [assignedUsers, setAssignedUsers] = useState([]);

  const [recipientMode, setRecipientMode] = useState(
    prefilledProgramId ? 'program' : 'recipients'
  );
  // Multi-recipient list: each entry is { type: 'internal'|'external', userId?, name?, email? }
  const [recipients, setRecipients] = useState(() => {
    if (prefilledUserId) {
      return [{ type: 'internal', userId: prefilledUserId, name: prefilledName || '', email: prefilledEmail || '' }];
    }
    return [];
  });
  const [addMode, setAddMode] = useState('external'); // 'internal' | 'external'
  const [addUserId, setAddUserId] = useState('');
  const [addUserName, setAddUserName] = useState('');
  const [addUserEmail, setAddUserEmail] = useState('');
  const [addExternalName, setAddExternalName] = useState('');
  const [addExternalEmail, setAddExternalEmail] = useState('');
  const [programId, setProgramId] = useState(prefilledProgramId || '');
  const [subject, setSubject] = useState('');
  const [body, setBody] = useState('');
  const [emailType, setEmailType] = useState('coaching');
  const [customInstructions, setCustomInstructions] = useState('');
  const [viewMode, setViewMode] = useState('edit'); // 'edit' | 'preview' | 'branded'
  const [aiError, setAiError] = useState(null);
  const [language, setLanguage] = useState('de');

  // Hero block (App Store style) — optional, off by default
  const [heroEnabled, setHeroEnabled] = useState(false);
  const [heroHeadline, setHeroHeadline] = useState('');
  const [heroAccentWord, setHeroAccentWord] = useState('');
  const [heroTagline, setHeroTagline] = useState('');

  // Vimeo videos — list of URLs to embed as play-thumbnail cards
  const [videoInput, setVideoInput] = useState('');
  const [videos, setVideos] = useState([]);

  // Bulk send confirmation
  const [showBulkConfirm, setShowBulkConfirm] = useState(false);
  const [bulkConfirmInput, setBulkConfirmInput] = useState('');
  const [bulkSendLockUntil, setBulkSendLockUntil] = useState(null);
  const [duplicateWarning, setDuplicateWarning] = useState(null);
  const [checkingDuplicate, setCheckingDuplicate] = useState(false);

  // Transactional override — lets a 1:1 service email reach a user who opted
  // out of marketing. Single-user only; requires a reason for the audit log.
  const [transactional, setTransactional] = useState(false);
  const [transactionalReason, setTransactionalReason] = useState('');

  // 1:1 sends to a platform user are coaching emails: they ignore marketing consent
  // but respect the user's coaching opt-out. Support can only send coaching emails;
  // sends to several recipients stay marketing.
  const [messageClass, setMessageClass] = useState('coaching');
  const [coachingOptOuts, setCoachingOptOuts] = useState({});
  const [draftLoading, setDraftLoading] = useState(false);
  const [draftLoaded, setDraftLoaded] = useState(null);

  // Inline image upload (paste / drag-drop / button)
  const textareaRef = useRef(null);
  const fileInputRef = useRef(null);
  const [uploading, setUploading] = useState(false);
  const [dragOver, setDragOver] = useState(false);

  const insertAtCursor = (text) => {
    const ta = textareaRef.current;
    if (!ta) {
      setBody((prev) => (prev ? prev + text : text));
      return;
    }
    const start = ta.selectionStart ?? body.length;
    const end = ta.selectionEnd ?? body.length;
    const next = body.slice(0, start) + text + body.slice(end);
    setBody(next);
    requestAnimationFrame(() => {
      ta.focus();
      const pos = start + text.length;
      ta.setSelectionRange(pos, pos);
    });
  };

  const handleImageFile = async (file) => {
    if (!isSupportedImage(file)) {
      toast.error('Only PNG, JPEG, GIF or WebP images are supported');
      return;
    }
    setUploading(true);
    setViewMode('edit'); // ensure user sees the inserted markup
    try {
      const { url } = await uploadEmailImage(file);
      // Assets are designed at their intended dimensions, so we don't impose
      // a width or border-radius. Only safety nets:
      //   - figure wrapper centers the image with consistent vertical margin
      //   - max-width:100% prevents overflow on phones / narrow clients
      const figure = `\n<figure style="margin:24px 0;text-align:center;">\n  <img src="${url}" alt="" style="display:block;max-width:100%;height:auto;margin:0 auto;"/>\n</figure>\n`;
      insertAtCursor(figure);
      toast.success('Image inserted');
    } catch (err) {
      toast.error(`Image upload failed: ${err.message}`);
    } finally {
      setUploading(false);
    }
  };

  const handleBodyPaste = async (e) => {
    const items = e.clipboardData?.items;
    if (!items) return;
    for (const item of items) {
      if (item.kind === 'file' && item.type.startsWith('image/')) {
        e.preventDefault();
        const file = item.getAsFile();
        if (file) await handleImageFile(file);
        return;
      }
    }
  };

  const handleBodyDrop = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragOver(false);
    const files = Array.from(e.dataTransfer?.files || []);
    const images = files.filter((f) => f.type.startsWith('image/'));
    if (images.length === 0) return;
    for (const file of images) {
      // Sequential to keep cursor inserts in order
      await handleImageFile(file);
    }
  };

  const handleDragOver = (e) => {
    if (e.dataTransfer?.types?.includes('Files')) {
      e.preventDefault();
      setDragOver(true);
    }
  };

  const handleDragLeave = (e) => {
    if (e.currentTarget.contains(e.relatedTarget)) return;
    setDragOver(false);
  };

  const handleFileSelect = async (e) => {
    const file = e.target.files?.[0];
    if (file) await handleImageFile(file);
    e.target.value = '';
  };

  const handleAddVideo = () => {
    const trimmed = videoInput.trim();
    if (!trimmed) return;
    const id = extractVimeoId(trimmed);
    if (!id) {
      toast.error("Couldn't recognize that as a Vimeo URL. Paste the page URL like https://vimeo.com/123456789");
      return;
    }
    if (videos.includes(trimmed)) {
      toast.info('Already added');
      setVideoInput('');
      return;
    }
    setVideos([...videos, trimmed]);
    setVideoInput('');
  };

  const handleRemoveVideo = (url) => {
    setVideos(videos.filter((v) => v !== url));
  };

  const buildHero = () => {
    if (!heroEnabled || !heroHeadline.trim()) return null;
    return {
      headline: heroHeadline,
      accentWord: heroAccentWord || undefined,
      tagline: heroTagline || undefined,
    };
  };

  const handleAddRecipient = () => {
    if (addMode === 'internal') {
      if (!addUserId.trim() && !addUserEmail.trim()) {
        toast.error('Enter a User ID or email');
        return;
      }
      const exists = recipients.some((r) => r.type === 'internal' && r.userId === addUserId.trim());
      if (exists) { toast.info('Already added'); return; }
      setRecipients([...recipients, {
        type: 'internal',
        userId: addUserId.trim(),
        name: addUserName.trim(),
        email: addUserEmail.trim(),
      }]);
      setAddUserId('');
      setAddUserName('');
      setAddUserEmail('');
    } else {
      if (!addExternalEmail.trim()) {
        toast.error('Enter an email address');
        return;
      }
      const exists = recipients.some((r) => r.type === 'external' && r.email === addExternalEmail.trim());
      if (exists) { toast.info('Already added'); return; }
      setRecipients([...recipients, {
        type: 'external',
        name: addExternalName.trim(),
        email: addExternalEmail.trim(),
      }]);
      setAddExternalName('');
      setAddExternalEmail('');
    }
  };

  const handleRemoveRecipient = (index) => {
    setRecipients(recipients.filter((_, i) => i !== index));
  };

  const resolveRecipientsForSend = () => {
    if (recipients.length > 0) return recipients;

    if (addMode === 'external' && addExternalEmail.trim()) {
      return [{
        type: 'external',
        name: addExternalName.trim(),
        email: addExternalEmail.trim(),
      }];
    }

    if (addMode === 'internal' && (addUserId.trim() || addUserEmail.trim())) {
      return [{
        type: 'internal',
        userId: addUserId.trim(),
        name: addUserName.trim(),
        email: addUserEmail.trim(),
      }];
    }

    return recipients;
  };

  const internalRecipientIds = recipients
    .filter((r) => r.type === 'internal' && r.userId)
    .map((r) => r.userId);
  const isMultiRecipient = recipients.length > 1;
  const effectiveMessageClass = !canBroadcast ? 'coaching' : isMultiRecipient ? 'marketing' : messageClass;
  const showMessageClass = recipientMode === 'recipients' && internalRecipientIds.length > 0 && !transactional;
  const isCoachingSend = showMessageClass && effectiveMessageClass === 'coaching';
  const coachingOptOutAt = isCoachingSend
    ? internalRecipientIds.map((id) => coachingOptOuts[id]).find(Boolean)
    : null;
  const optOutLookupKey = internalRecipientIds.join(',');

  useEffect(() => {
    if (!optOutLookupKey) {
      setCoachingOptOuts({});
      return;
    }
    let cancelled = false;
    supabase
      .from('email_preferences')
      .select('user_id, opted_out_at')
      .eq('category', 'coaching')
      .eq('opted_out', true)
      .in('user_id', optOutLookupKey.split(','))
      .then(({ data, error }) => {
        if (cancelled) return;
        if (error) {
          console.error('Error fetching coaching email preferences:', error);
          setCoachingOptOuts({});
          return;
        }
        setCoachingOptOuts(Object.fromEntries((data || []).map((r) => [r.user_id, r.opted_out_at || true])));
      });
    return () => { cancelled = true; };
  }, [optOutLookupKey]);

  useEffect(() => {
    if (canBroadcast) fetchPrograms();
  }, [fetchPrograms, canBroadcast]);

  useEffect(() => {
    if (canBroadcast) return;
    supabase.rpc('staff_get_assigned_users').then(({ data, error }) => {
      if (error) {
        console.error('Error fetching assigned users:', error);
        return;
      }
      setAssignedUsers(data || []);
    });
  }, [canBroadcast]);

  const handleSelectAssignedUser = (userId) => {
    const user = assignedUsers.find((u) => u.user_id === userId);
    if (!user) return;
    setRecipients([{ type: 'internal', userId: user.user_id, name: user.full_name || '', email: user.email || '' }]);
  };

  useEffect(() => {
    if (prefilledUserId) {
      setRecipients([{ type: 'internal', userId: prefilledUserId, name: prefilledName || '', email: prefilledEmail || '' }]);
      setRecipientMode('recipients');
    }
  }, [prefilledUserId, prefilledName, prefilledEmail]);

  useEffect(() => {
    if (prefilledProgramId && canBroadcast) {
      setProgramId(prefilledProgramId);
      setRecipientMode('program');
    }
  }, [prefilledProgramId, canBroadcast]);

  useEffect(() => {
    if (!canBroadcast && recipientMode !== 'recipients') setRecipientMode('recipients');
  }, [canBroadcast, recipientMode]);

  useEffect(() => {
    if (!prefilledDraft) return;

    let cancelled = false;
    setDraftLoading(true);

    (async () => {
      try {
        const metaRes = await fetch(`/email-drafts/${prefilledDraft}.json`);
        if (!metaRes.ok) throw new Error(`Draft "${prefilledDraft}" not found`);
        const meta = await metaRes.json();

        const bodyRes = await fetch(`/email-drafts/${meta.bodyFile || `${prefilledDraft}-body.html`}`);
        if (!bodyRes.ok) throw new Error('Draft body HTML not found');
        const bodyHtml = await bodyRes.text();

        if (cancelled) return;

        setSubject(meta.subject || '');
        setBody(bodyHtml.trim());
        if (meta.emailType) setEmailType(meta.emailType);
        if (meta.hero?.headline) {
          setHeroEnabled(true);
          setHeroHeadline(meta.hero.headline || '');
          setHeroAccentWord(meta.hero.accentWord || '');
          setHeroTagline(meta.hero.tagline || '');
        }
        if (meta.transactional) {
          setTransactional(true);
          setTransactionalReason(meta.transactionalReason || '');
        }
        if (meta.language === 'en' || meta.language === 'de') {
          setLanguage(meta.language);
        }
        setDraftLoaded(prefilledDraft);
        setViewMode('branded');
        toast.success(`Loaded draft "${prefilledDraft}"`);
      } catch (err) {
        if (!cancelled) toast.error(`Draft load failed: ${err.message}`);
      } finally {
        if (!cancelled) setDraftLoading(false);
      }
    })();

    return () => { cancelled = true; };
  }, [prefilledDraft]);

  useEffect(() => {
    if (generatedEmails.length > 0 && !generatedEmails[0].error) {
      setSubject(generatedEmails[0].subject);
      setBody(generatedEmails[0].html);
    }
  }, [generatedEmails]);

  const hasDraft = subject.trim().length > 0 || body.trim().length > 0;

  const handlePolish = async () => {
    if (!body.trim()) {
      setAiError('Write your draft in the body above first, then polish it.');
      return;
    }
    setAiError(null);
    try {
      const internalIds = recipients.filter((r) => r.type === 'internal').map((r) => r.userId);
      await polishWithAI({
        userIds: internalIds,
        emailType,
        draftSubject: subject,
        draftBody: body,
        customInstructions: customInstructions || undefined,
      });
      toast.success('Draft polished by Opus');
    } catch (err) {
      setAiError(`Polish failed: ${err.message}`);
    }
  };

  const handleGenerate = async () => {
    const internalRecipients = recipients.filter((r) => r.type === 'internal');
    if (internalRecipients.length === 0) {
      setAiError('Add a platform user first — AI needs their data to generate a personalised email.');
      return;
    }
    setAiError(null);
    try {
      await generateWithAI({
        userIds: internalRecipients.map((r) => r.userId),
        emailType,
        customInstructions: customInstructions || undefined,
      });
      toast.success('AI content generated');
    } catch (err) {
      setAiError(`Generation failed: ${err.message}`);
    }
  };

  const executeSend = async (isTest = false) => {
    try {
      const baseParams = {
        subject,
        html: body.includes('<') ? body : `<p>${body.replace(/\n/g, '<br/>')}</p>`,
        isTest,
        hero: buildHero(),
        videos,
        language,
      };

      if (recipientMode === 'allConsented') {
        const result = await sendEmail({ ...baseParams, allConsented: true });
        if (isTest) {
          toast.success('Test email sent to your inbox');
        } else if (result.sent !== undefined) {
          toast.success(`${result.sent} email(s) sent successfully`);
        } else {
          toast.success('Email sent successfully');
        }
        return;
      }

      if (recipientMode === 'program') {
        if (!programId) { toast.error('Select a program'); return; }
        const result = await sendEmail({ ...baseParams, programId });
        if (isTest) {
          toast.success('Test email sent to your inbox');
        } else if (result.sent !== undefined) {
          toast.success(`${result.sent} email(s) sent successfully`);
        } else {
          toast.success('Email sent successfully');
        }
        return;
      }

      const sendRecipients = resolveRecipientsForSend();

      if (sendRecipients.length === 0) {
        toast.error('Add at least one recipient (External tab → name + email → +, or fill the fields and send)');
        return;
      }

      if (transactional && !isTest && !transactionalReason.trim()) {
        toast.error('Add a reason for the transactional override before sending.');
        return;
      }

      let sent = 0;
      let failed = 0;
      let lastError = null;

      for (const recipient of sendRecipients) {
        try {
          const params = { ...baseParams };
          if (recipient.type === 'internal') {
            params.userId = recipient.userId;
            params.toEmail = recipient.email;
            if (transactional && !isTest) {
              params.transactional = true;
              params.transactionalReason = transactionalReason.trim();
            } else {
              params.messageClass = effectiveMessageClass;
            }
          } else {
            params.toEmail = recipient.email;
            params.toName = recipient.name || undefined;
            params.external = true;
          }
          await sendEmail(params);
          sent++;
        } catch (err) {
          failed++;
          lastError = err.message;
          console.error(`Failed to send to ${recipient.email}:`, err.message);
        }
      }

      if (isTest) {
        toast.success(`Test email sent to your inbox (${sendRecipients.length} recipient${sendRecipients.length > 1 ? 's' : ''} previewed)`);
      } else if (failed === 0) {
        toast.success(`${sent} email(s) sent successfully`);
      } else {
        toast.error(lastError || `${sent} sent, ${failed} failed`);
      }
    } catch (err) {
      toast.error(`Send failed: ${err.message}`);
    }
  };

  const isBulkLocked = bulkSendLockUntil && Date.now() < bulkSendLockUntil;

  const getBulkRecipientCount = () => {
    if (recipientMode === 'allConsented') return 'ALL';
    if (recipientMode === 'program' && selectedProgram) return String(selectedProgram.active_user_count);
    if (recipientMode === 'recipients') return String(recipients.length);
    return '';
  };

  const handleSend = async (isTest = false) => {
    if (!subject || !body) {
      toast.error('Subject and body are required');
      return;
    }

    const isBulk = (recipientMode === 'allConsented') ||
      (recipientMode === 'program' && selectedProgram) ||
      (recipientMode === 'recipients' && recipients.length > 1);

    if (!isTest && isBulk) {
      if (isBulkLocked) {
        const remainMin = Math.ceil((bulkSendLockUntil - Date.now()) / 60000);
        toast.error(`Bulk send locked for ${remainMin} more minute(s) to prevent duplicates.`);
        return;
      }

      setCheckingDuplicate(true);
      const dup = await checkRecentBulkSend(subject);
      setCheckingDuplicate(false);

      if (dup) {
        const sentTime = new Date(dup.sentAt).toLocaleTimeString();
        setDuplicateWarning(`This subject was already sent at ${sentTime} today. Sending again will deliver duplicate emails.`);
      } else {
        setDuplicateWarning(null);
      }

      setBulkConfirmInput('');
      setShowBulkConfirm(true);
      return;
    }

    await executeSend(isTest);
  };

  const handleConfirmBulkSend = async () => {
    const count = getBulkRecipientCount();
    if (count !== bulkConfirmInput.trim()) {
      toast.error(`Type "${count}" to confirm the send.`);
      return;
    }
    setShowBulkConfirm(false);
    setBulkConfirmInput('');
    setDuplicateWarning(null);
    await executeSend(false);
    setBulkSendLockUntil(Date.now() + 5 * 60 * 1000);
  };

  const selectedProgram = programs.find((p) => p.program_id === programId);

  const renderBodyContent = () => {
    const rawHtml = body.includes('<') ? body : `<p>${body.replace(/\n/g, '<br/>')}</p>`;

    if (viewMode === 'edit') {
      return (
        <Textarea
          ref={textareaRef}
          placeholder="Write your email here — your ideas, notes, key points. Paste a screenshot (Cmd+V) or drag an image to embed it. Then use 'Polish with AI' or 'Generate from Scratch'."
          value={body}
          onChange={(e) => setBody(e.target.value)}
          onPaste={handleBodyPaste}
          rows={12}
          className="font-mono text-sm"
        />
      );
    }

    if (viewMode === 'preview') {
      return (
        <div
          className="min-h-[200px] p-4 border rounded-md bg-white dark:bg-zinc-950 prose prose-sm max-w-none"
          dangerouslySetInnerHTML={{ __html: rawHtml }}
        />
      );
    }

    // branded preview — pass hero + videos so user can see them in context.
    // BrandedPreviewIframe auto-resizes to fit the rendered email so wide
    // hero assets / long bodies aren't truncated by a fixed iframe height.
    return (
      <BrandedPreviewIframe html={wrapInTemplate(rawHtml, { hero: buildHero(), videos, recipientName: recipients[0]?.name || '', language, messageClass: isCoachingSend ? 'coaching' : undefined })} />
    );
  };

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle className="text-base flex items-center gap-2">
            Compose Email
            {draftLoading && <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />}
            {draftLoaded && !draftLoading && (
              <span className="text-xs font-normal text-muted-foreground">Draft: {draftLoaded}</span>
            )}
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* Recipient Mode */}
          <div className="space-y-2">
            <Label>Recipients</Label>
            {canBroadcast && (
            <div className="flex gap-2 flex-wrap">
              <Button
                variant={recipientMode === 'recipients' ? 'default' : 'outline'}
                size="sm"
                onClick={() => setRecipientMode('recipients')}
              >
                Recipients
              </Button>
              <Button
                variant={recipientMode === 'program' ? 'default' : 'outline'}
                size="sm"
                onClick={() => setRecipientMode('program')}
              >
                By Program
              </Button>
              <Button
                variant={recipientMode === 'allConsented' ? 'default' : 'outline'}
                size="sm"
                onClick={() => setRecipientMode('allConsented')}
              >
                <Megaphone className="h-3.5 w-3.5 mr-1.5" />
                All Consented
              </Button>
            </div>
            )}
          </div>

          {recipientMode === 'recipients' ? (
            <div className="space-y-3">
              {/* Added recipients list */}
              {recipients.length > 0 && (
                <div className="space-y-2">
                  {recipients.map((r, i) => (
                    <div key={i} className="flex items-center gap-2 p-2.5 rounded-md bg-muted/50 border">
                      {r.type === 'internal' ? (
                        <Users className="h-4 w-4 text-blue-500 shrink-0" />
                      ) : (
                        <Mail className="h-4 w-4 text-emerald-500 shrink-0" />
                      )}
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium truncate">
                          {r.name || r.userId || r.email}
                          <span className="ml-2 text-xs font-normal text-muted-foreground">
                            {r.type === 'internal' ? '(platform user)' : '(external)'}
                          </span>
                        </p>
                        {r.email && r.name && (
                          <p className="text-xs text-muted-foreground truncate">{r.email}</p>
                        )}
                      </div>
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-7 w-7 shrink-0"
                        onClick={() => handleRemoveRecipient(i)}
                      >
                        <X className="h-4 w-4" />
                      </Button>
                    </div>
                  ))}
                </div>
              )}

              {!canBroadcast ? (
                <Select value={recipients[0]?.userId || ''} onValueChange={handleSelectAssignedUser}>
                  <SelectTrigger>
                    <SelectValue placeholder={assignedUsers.length ? 'Select an assigned user...' : 'No users assigned to you'} />
                  </SelectTrigger>
                  <SelectContent>
                    {assignedUsers.map((u) => (
                      <SelectItem key={u.user_id} value={u.user_id}>
                        {u.full_name ? `${u.full_name}${u.email ? ` (${u.email})` : ''}` : u.email || u.user_id}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              ) : (
              /* Add recipient form */
              <div className="p-3 border border-dashed rounded-lg space-y-3">
                <div className="flex items-center gap-2">
                  <span className="text-xs font-medium text-muted-foreground">Add recipient:</span>
                  <Button
                    variant={addMode === 'internal' ? 'secondary' : 'ghost'}
                    size="sm"
                    onClick={() => setAddMode('internal')}
                    className="h-6 px-2 text-xs"
                  >
                    <Users className="h-3 w-3 mr-1" />
                    Platform User
                  </Button>
                  <Button
                    variant={addMode === 'external' ? 'secondary' : 'ghost'}
                    size="sm"
                    onClick={() => setAddMode('external')}
                    className="h-6 px-2 text-xs"
                  >
                    <Mail className="h-3 w-3 mr-1" />
                    External
                  </Button>
                </div>

                {addMode === 'internal' ? (
                  <div className="flex gap-2">
                    <Input
                      placeholder="User ID"
                      value={addUserId}
                      onChange={(e) => setAddUserId(e.target.value)}
                      className="flex-1"
                    />
                    <Input
                      placeholder="Name (optional)"
                      value={addUserName}
                      onChange={(e) => setAddUserName(e.target.value)}
                      className="flex-1"
                    />
                    <Input
                      placeholder="Email (optional)"
                      value={addUserEmail}
                      onChange={(e) => setAddUserEmail(e.target.value)}
                      className="flex-1"
                    />
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={handleAddRecipient}
                      className="shrink-0"
                    >
                      <Plus className="h-4 w-4" />
                    </Button>
                  </div>
                ) : (
                  <div className="space-y-2">
                    <div className="flex gap-2">
                      <Input
                        placeholder="Name"
                        value={addExternalName}
                        onChange={(e) => setAddExternalName(e.target.value)}
                        className="flex-1"
                      />
                      <Input
                        placeholder="Email"
                        type="email"
                        value={addExternalEmail}
                        onChange={(e) => setAddExternalEmail(e.target.value)}
                        className="flex-1"
                        onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); handleAddRecipient(); } }}
                      />
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={handleAddRecipient}
                        className="shrink-0"
                      >
                        <Plus className="h-4 w-4" />
                      </Button>
                    </div>
                    <p className="text-xs text-muted-foreground">
                      For deleted accounts or non-platform contacts: add name + email, click +, then send. You can also send directly if both fields are filled.
                    </p>
                  </div>
                )}
              </div>
              )}
            </div>
          ) : recipientMode === 'program' ? (
            <div className="space-y-2">
              <Select value={programId} onValueChange={setProgramId}>
                <SelectTrigger>
                  <SelectValue placeholder="Select program..." />
                </SelectTrigger>
                <SelectContent>
                  {programs.map((p) => (
                    <SelectItem key={p.program_id} value={p.program_id}>
                      {p.program_name} ({p.active_user_count} users)
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {selectedProgram && (
                <p className="text-xs text-muted-foreground">
                  Will send to {selectedProgram.active_user_count} active users
                </p>
              )}
            </div>
          ) : (
            <div className="flex items-start gap-3 p-4 border rounded-lg bg-muted/30">
              <Megaphone className="h-5 w-5 text-lime-500 mt-0.5 shrink-0" />
              <div className="space-y-1">
                <p className="text-sm font-medium">All Marketing-Consented Users</p>
                <p className="text-xs text-muted-foreground">
                  This will send to every user who opted in to marketing emails (excluding admins). Use Test Send first to preview.
                </p>
              </div>
            </div>
          )}

          {showMessageClass && (
            <div className="space-y-2 p-4 border rounded-lg">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2 text-sm font-medium">
                  <Mail className="h-4 w-4 text-muted-foreground" />
                  {effectiveMessageClass === 'coaching' ? 'Coaching email' : 'Marketing email'}
                </div>
                {canBroadcast && !isMultiRecipient && (
                  <div className="flex gap-1">
                    <Button
                      variant={messageClass === 'coaching' ? 'default' : 'outline'}
                      size="sm"
                      onClick={() => setMessageClass('coaching')}
                      className="h-7 px-3 text-xs"
                    >
                      Coaching
                    </Button>
                    <Button
                      variant={messageClass === 'marketing' ? 'default' : 'outline'}
                      size="sm"
                      onClick={() => setMessageClass('marketing')}
                      className="h-7 px-3 text-xs"
                    >
                      Marketing
                    </Button>
                  </div>
                )}
              </div>
              <p className="text-xs text-muted-foreground">
                {effectiveMessageClass === 'coaching'
                  ? 'Coaching email: training communication only. No offers, prices, upsells or store links.'
                  : isMultiRecipient
                    ? 'Several recipients: sent as marketing emails, only to users who accepted marketing emails.'
                    : 'Marketing email: only sent if the user accepted marketing emails.'}
              </p>
              {coachingOptOutAt && (
                <div className="flex items-start gap-2 p-3 rounded-md bg-destructive/10 text-destructive text-xs">
                  <AlertCircle className="h-4 w-4 mt-0.5 shrink-0" />
                  <span>
                    This athlete unsubscribed from coaching emails
                    {typeof coachingOptOutAt === 'string' ? ` on ${new Date(coachingOptOutAt).toLocaleDateString()}` : ''}.
                    {' '}Coaching emails can&apos;t be sent to them.
                  </span>
                </div>
              )}
            </div>
          )}

          {/* Transactional override — shown when internal users are in the list */}
          {canBroadcast && recipientMode === 'recipients' && recipients.some((r) => r.type === 'internal') && (
            <div className={`space-y-3 p-4 border rounded-lg ${transactional ? 'border-amber-500/60 bg-amber-500/5' : ''}`}>
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2 text-sm font-medium">
                  <ShieldAlert className={`h-4 w-4 ${transactional ? 'text-amber-500' : 'text-muted-foreground'}`} />
                  Transactional override
                  <span className="text-xs text-muted-foreground font-normal">— send a 1:1 service email to a user who opted out of marketing</span>
                </div>
                <Button
                  variant={transactional ? 'default' : 'outline'}
                  size="sm"
                  onClick={() => setTransactional(!transactional)}
                  className="h-7 px-3 text-xs"
                >
                  {transactional ? 'On' : 'Off'}
                </Button>
              </div>
              {transactional && (
                <div className="space-y-2 pt-1">
                  <div className="flex items-start gap-2 p-3 rounded-md bg-amber-500/10 text-amber-700 dark:text-amber-400 text-xs">
                    <AlertCircle className="h-4 w-4 mt-0.5 shrink-0" />
                    <span>
                      Use only for genuine service messages (coach reply, onboarding help, account issues) — not marketing.
                      This bypasses the marketing-consent check but does <strong>not</strong> change the user's consent or Brevo status. The reason is stored in the audit log.
                    </span>
                  </div>
                  <Label className="text-xs">Reason (required)</Label>
                  <Input
                    placeholder="e.g. 1:1 onboarding help for new trial user"
                    value={transactionalReason}
                    onChange={(e) => setTransactionalReason(e.target.value)}
                  />
                </div>
              )}
            </div>
          )}

          {/* Email Type */}
          <div className="space-y-2">
            <Label>Email Type</Label>
            <Select value={emailType} onValueChange={setEmailType}>
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="coaching">Coaching</SelectItem>
                <SelectItem value="progress">Progress Report</SelectItem>
                <SelectItem value="reminder">Reminder</SelectItem>
                <SelectItem value="onboarding">Onboarding</SelectItem>
                <SelectItem value="campaign">Campaign</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {/* Language */}
          <div className="space-y-2">
            <Label>Language</Label>
            <div className="flex gap-2">
              <Button
                variant={language === 'de' ? 'default' : 'outline'}
                size="sm"
                onClick={() => setLanguage('de')}
              >
                Deutsch
              </Button>
              <Button
                variant={language === 'en' ? 'default' : 'outline'}
                size="sm"
                onClick={() => setLanguage('en')}
              >
                English
              </Button>
            </div>
            <p className="text-xs text-muted-foreground">
              Controls greeting, buttons, and footer language in the branded template.
            </p>
          </div>

          {/* Subject */}
          <div className="space-y-2">
            <Label>Subject</Label>
            <Input
              placeholder="Email subject line"
              value={subject}
              onChange={(e) => setSubject(e.target.value)}
            />
          </div>

          {/* Body with view mode toggle */}
          <div className="space-y-2">
            <div className="flex items-center justify-between flex-wrap gap-2">
              <Label>Body</Label>
              <div className="flex items-center gap-1 flex-wrap">
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/png,image/jpeg,image/gif,image/webp"
                  className="hidden"
                  onChange={handleFileSelect}
                />
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => fileInputRef.current?.click()}
                  disabled={uploading}
                  className="h-7 px-2 text-xs"
                  title="Insert an image (or paste / drag one onto the body)"
                >
                  {uploading ? (
                    <Loader2 className="h-3 w-3 mr-1 animate-spin" />
                  ) : (
                    <ImagePlus className="h-3 w-3 mr-1" />
                  )}
                  {uploading ? 'Uploading...' : 'Insert image'}
                </Button>
                <div className="w-px h-5 bg-border mx-1" aria-hidden />
                <Button
                  variant={viewMode === 'edit' ? 'secondary' : 'ghost'}
                  size="sm"
                  onClick={() => setViewMode('edit')}
                  className="h-7 px-2 text-xs"
                >
                  <PenLine className="h-3 w-3 mr-1" />
                  Edit
                </Button>
                <Button
                  variant={viewMode === 'preview' ? 'secondary' : 'ghost'}
                  size="sm"
                  onClick={() => setViewMode('preview')}
                  className="h-7 px-2 text-xs"
                >
                  <Code className="h-3 w-3 mr-1" />
                  Preview
                </Button>
                <Button
                  variant={viewMode === 'branded' ? 'secondary' : 'ghost'}
                  size="sm"
                  onClick={() => setViewMode('branded')}
                  className="h-7 px-2 text-xs"
                >
                  <Eye className="h-3 w-3 mr-1" />
                  Branded
                </Button>
              </div>
            </div>
            <div
              onDragOver={handleDragOver}
              onDragEnter={handleDragOver}
              onDragLeave={handleDragLeave}
              onDrop={handleBodyDrop}
              className={`relative rounded-md transition-all ${
                dragOver ? 'ring-2 ring-lime-500 ring-offset-2 ring-offset-background' : ''
              }`}
            >
              {renderBodyContent()}
              {dragOver && (
                <div className="absolute inset-0 flex items-center justify-center bg-lime-500/10 border-2 border-dashed border-lime-500 rounded-md pointer-events-none z-10">
                  <div className="flex items-center gap-2 px-4 py-2 bg-background border rounded-full shadow-sm text-sm font-medium">
                    <Upload className="h-4 w-4" />
                    Drop image to embed
                  </div>
                </div>
              )}
              {uploading && !dragOver && (
                <div className="absolute top-2 right-2 flex items-center gap-2 px-3 py-1.5 bg-background border rounded-full shadow-sm text-xs font-medium">
                  <Loader2 className="h-3 w-3 animate-spin" />
                  Uploading...
                </div>
              )}
            </div>
            <p className="text-xs text-muted-foreground">
              Tip: paste a screenshot (Cmd+V) or drag an image onto the body to embed it inline.
            </p>
          </div>

          {/* Hero Block (App Store style) */}
          <div className="space-y-3 p-4 border rounded-lg">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2 text-sm font-medium">
                <Megaphone className="h-4 w-4" />
                Hero block
                <span className="text-xs text-muted-foreground font-normal">— App Store style headline on lime</span>
              </div>
              <Button
                variant={heroEnabled ? 'default' : 'outline'}
                size="sm"
                onClick={() => setHeroEnabled(!heroEnabled)}
                className="h-7 px-3 text-xs"
              >
                {heroEnabled ? 'Enabled' : 'Add hero'}
              </Button>
            </div>
            {heroEnabled && (
              <div className="space-y-3 pt-2">
                <div className="space-y-1.5">
                  <Label className="text-xs">Headline</Label>
                  <Input
                    placeholder="e.g. your life companion for health and fitness."
                    value={heroHeadline}
                    onChange={(e) => setHeroHeadline(e.target.value)}
                  />
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-1.5">
                    <Label className="text-xs">Accent word (underlined)</Label>
                    <Input
                      placeholder="e.g. companion"
                      value={heroAccentWord}
                      onChange={(e) => setHeroAccentWord(e.target.value)}
                    />
                  </div>
                  <div className="space-y-1.5">
                    <Label className="text-xs">Tagline</Label>
                    <Input
                      placeholder="e.g. one place to train & build your health."
                      value={heroTagline}
                      onChange={(e) => setHeroTagline(e.target.value)}
                    />
                  </div>
                </div>
                <p className="text-xs text-muted-foreground">
                  Switch to <span className="font-medium">Branded</span> preview to see the hero block render.
                </p>
              </div>
            )}
          </div>

          {/* Vimeo Videos */}
          <div className="space-y-3 p-4 border rounded-lg">
            <div className="flex items-center gap-2 text-sm font-medium">
              <Video className="h-4 w-4" />
              Videos
              <span className="text-xs text-muted-foreground font-normal">— paste Vimeo links</span>
            </div>
            <div className="flex gap-2">
              <Input
                placeholder="https://vimeo.com/123456789"
                value={videoInput}
                onChange={(e) => setVideoInput(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    handleAddVideo();
                  }
                }}
                className="flex-1"
              />
              <Button
                variant="outline"
                size="sm"
                onClick={handleAddVideo}
                disabled={!videoInput.trim()}
              >
                <Plus className="h-4 w-4 mr-1" />
                Add
              </Button>
            </div>
            {videos.length > 0 ? (
              <div className="space-y-2">
                {videos.map((url) => {
                  const id = extractVimeoId(url);
                  return (
                    <div key={url} className="flex items-center gap-3 p-2 rounded-md bg-muted/40 border">
                      {id ? (
                        <img
                          src={`https://vumbnail.com/${id}_small.jpg`}
                          alt=""
                          className="h-12 w-20 object-cover rounded bg-zinc-900"
                          onError={(e) => { e.currentTarget.style.visibility = 'hidden'; }}
                        />
                      ) : null}
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium truncate">{url}</p>
                        <p className="text-xs text-muted-foreground">Vimeo ID: {id || 'unknown'}</p>
                      </div>
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-7 w-7 shrink-0"
                        onClick={() => handleRemoveVideo(url)}
                      >
                        <X className="h-4 w-4" />
                      </Button>
                    </div>
                  );
                })}
              </div>
            ) : (
              <p className="text-xs text-muted-foreground">
                Recipients see a click-to-play thumbnail that opens the video on Vimeo.
              </p>
            )}
          </div>

          {/* AI Section */}
          <div className="space-y-3 p-4 border rounded-lg bg-muted/30">
            <div className="flex items-center gap-2 text-sm font-medium">
              <Sparkles className="h-4 w-4" />
              AI Assistant
            </div>
            <Textarea
              placeholder="Instructions for AI — e.g. 'Focus on their recent pain levels and suggest lighter sessions this week' or 'Make the tone more celebratory, they just hit a milestone'"
              value={customInstructions}
              onChange={(e) => setCustomInstructions(e.target.value)}
              rows={2}
              className="text-sm"
            />
            <div className="flex gap-2">
              <Button
                variant="default"
                size="sm"
                onClick={handlePolish}
                disabled={generating || !hasDraft}
                title={!hasDraft ? 'Write a draft first, then polish it' : ''}
              >
                {generating ? (
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                ) : (
                  <Wand2 className="h-4 w-4 mr-2" />
                )}
                {generating ? 'Polishing...' : 'Polish with AI'}
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={handleGenerate}
                disabled={generating}
              >
                {generating ? (
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                ) : (
                  <Sparkles className="h-4 w-4 mr-2" />
                )}
                Generate from Scratch
              </Button>
            </div>
            {aiError && (
              <div className="flex items-start gap-2 p-3 rounded-md bg-destructive/10 text-destructive text-sm">
                <AlertCircle className="h-4 w-4 mt-0.5 shrink-0" />
                <span>{aiError}</span>
              </div>
            )}
            {!hasDraft && !aiError && (
              <p className="text-xs text-muted-foreground">
                Write your ideas in the body above, then click "Polish with AI" to have Opus improve your language and make it professional.
              </p>
            )}
          </div>

          {/* Actions */}
          <div className="flex items-center justify-between pt-2">
            <Button
              variant="outline"
              onClick={() => handleSend(true)}
              disabled={sending}
            >
              <TestTube className="h-4 w-4 mr-2" />
              Test Send
            </Button>
            <div className="flex items-center gap-3">
              {isBulkLocked && (
                <span className="text-xs text-amber-500 font-medium">
                  Send locked for {Math.ceil((bulkSendLockUntil - Date.now()) / 60000)}min
                </span>
              )}
              <Button
                onClick={() => handleSend(false)}
                disabled={sending || checkingDuplicate || !subject || !body || isBulkLocked || Boolean(coachingOptOutAt)}
              >
                {sending || checkingDuplicate ? (
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                ) : (
                  <Send className="h-4 w-4 mr-2" />
                )}
                {checkingDuplicate
                  ? 'Checking...'
                  : recipientMode === 'allConsented'
                    ? 'Send to All Consented'
                    : recipientMode === 'program' && selectedProgram
                      ? `Send to ${selectedProgram.active_user_count} users`
                      : recipients.length > 1
                        ? `Send to ${recipients.length} recipients`
                        : 'Send Email'}
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      <Dialog open={showBulkConfirm} onOpenChange={(open) => { if (!open) { setShowBulkConfirm(false); setBulkConfirmInput(''); setDuplicateWarning(null); } }}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <ShieldAlert className="h-5 w-5 text-destructive" />
              Confirm bulk send
            </DialogTitle>
          </DialogHeader>

          {duplicateWarning && (
            <div className="flex items-start gap-2 p-3 rounded-md bg-destructive/10 text-destructive text-sm border border-destructive/30">
              <AlertCircle className="h-5 w-5 mt-0.5 shrink-0" />
              <div>
                <p className="font-semibold">Duplicate send detected</p>
                <p className="mt-1">{duplicateWarning}</p>
              </div>
            </div>
          )}

          <p className="text-sm text-muted-foreground">
            {recipientMode === 'allConsented' ? (
              <>You are about to send emails to <span className="font-semibold text-foreground">all marketing-consented users</span> on the platform.</>
            ) : recipientMode === 'program' ? (
              <>You are about to send <span className="font-semibold text-foreground">{selectedProgram?.active_user_count || 0} emails</span> to
              all active users in <span className="font-semibold text-foreground">{selectedProgram?.program_name}</span>.</>
            ) : (
              <>You are about to send <span className="font-semibold text-foreground">{recipients.length} emails</span> to
              the listed recipients ({recipients.filter((r) => r.type === 'internal').length} platform, {recipients.filter((r) => r.type === 'external').length} external).</>
            )}
          </p>

          <div className="space-y-2 pt-2">
            <p className="text-sm font-medium text-destructive">
              This action sends real emails and cannot be undone.
            </p>
            <Label className="text-xs">
              Type <span className="font-mono font-bold text-foreground">{getBulkRecipientCount()}</span> to confirm:
            </Label>
            <Input
              placeholder={`Type ${getBulkRecipientCount()} to confirm`}
              value={bulkConfirmInput}
              onChange={(e) => setBulkConfirmInput(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); handleConfirmBulkSend(); } }}
              autoFocus
            />
          </div>

          <DialogFooter className="gap-2 sm:gap-0">
            <Button variant="outline" onClick={() => { setShowBulkConfirm(false); setBulkConfirmInput(''); setDuplicateWarning(null); }}>
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={handleConfirmBulkSend}
              disabled={sending || bulkConfirmInput.trim() !== getBulkRecipientCount()}
            >
              {sending ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Send className="h-4 w-4 mr-2" />}
              Confirm Send
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
